import React, { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { api } from '@/services/api';
import { Folder, ArrowUp, Loader2, AlertCircle, X, Check } from 'lucide-react';

interface FtpFolderPickerProps {
  value: string;
  onChange: (path: string) => void;
  label: string;
  placeholder?: string;
  disabled?: boolean;
  basePath?: string;
  compact?: boolean; // Compact single-row display mode
  companyId?: string; // Override company for multi-company users
}

interface FtpDirectory {
  name: string;
  path: string;
}

interface BrowseResponse {
  success: boolean;
  currentPath: string;
  directories: FtpDirectory[];
  files: Array<{ name: string; path: string; size: number }>;
  error?: string;
}

export const FtpFolderPicker: React.FC<FtpFolderPickerProps> = ({
  value,
  onChange,
  label,
  placeholder = 'Select a folder...',
  disabled = false,
  basePath,
  compact = false,
  companyId,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentPath, setCurrentPath] = useState('/');
  const [directories, setDirectories] = useState<FtpDirectory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const browseDirectory = useCallback(async (path: string) => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.post<BrowseResponse>('/ftp/browse', {
        path,
        companyId,
      });

      if (response.success && response.data) {
        if (response.data.success) {
          setCurrentPath(response.data.currentPath);
          setDirectories(response.data.directories);
        } else {
          setError(response.data.error || 'Failed to browse directory');
        }
      } else {
        setError(response.error?.message || 'Failed to browse directory');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to browse directory');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  const handleOpenDialog = () => {
    setIsOpen(true);
    // Start from current value, or basePath, or root
    const startPath = value || basePath || '/';
    browseDirectory(startPath);
  };

  const handleNavigateUp = () => {
    if (currentPath === '/') return;
    const parentPath = currentPath.substring(0, currentPath.lastIndexOf('/')) || '/';
    browseDirectory(parentPath);
  };

  const handleNavigateToDirectory = (dirPath: string) => {
    browseDirectory(dirPath);
  };

  const handleSelectCurrentFolder = () => {
    onChange(currentPath);
    setIsOpen(false);
  };

  const handleClear = () => {
    onChange('');
  };

  // Extract folder name from path for compact display
  const displayValue = value ? (value === '/' ? '/' : value.split('/').filter(Boolean).pop()) : null;

  // Compact mode: single row with status indicator
  if (compact) {
    return (
      <>
        <div className="flex items-center gap-3 py-2 px-1 hover:bg-muted/50 rounded-md group">
          {/* Status indicator */}
          <div
            className={`w-2 h-2 rounded-full shrink-0 ${
              value ? 'bg-green-500' : 'bg-muted-foreground/30'
            }`}
          />
          {/* Label */}
          <span className="w-28 text-sm font-medium shrink-0">{label}</span>
          {/* Value */}
          <span
            className={`flex-1 text-sm truncate ${
              value ? 'text-foreground font-mono' : 'text-muted-foreground italic'
            }`}
          >
            {displayValue || 'Not set'}
          </span>
          {/* Clear button */}
          {value && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <X className="h-4 w-4" />
            </button>
          )}
          {/* Browse button */}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleOpenDialog}
            disabled={disabled}
          >
            Browse
          </Button>
        </div>

        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
            <DialogHeader>
              <DialogTitle>Select Folder for {label}</DialogTitle>
              <DialogDescription>
                Navigate to and select the folder where files should be stored
              </DialogDescription>
            </DialogHeader>

            {/* Current path and navigation */}
            <div className="flex items-center gap-2 py-2 border-b">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleNavigateUp}
                disabled={currentPath === '/' || loading}
              >
                <ArrowUp className="h-4 w-4 mr-1" />
                Up
              </Button>
              <span className="text-sm text-muted-foreground truncate flex-1">
                {currentPath}
              </span>
              <Button
                type="button"
                size="sm"
                onClick={handleSelectCurrentFolder}
                disabled={loading}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
              >
                <Check className="h-4 w-4 mr-1" />
                Select This Folder
              </Button>
            </div>

            {/* Error message */}
            {error && (
              <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-md">
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            {/* Directory browser */}
            <div className="flex-1 overflow-auto min-h-[300px]">
              {loading ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <div className="space-y-1">
                  {/* Directories */}
                  {directories.map((dir) => (
                    <button
                      key={dir.path}
                      type="button"
                      onClick={() => handleNavigateToDirectory(dir.path)}
                      className="w-full flex items-center gap-3 px-3 py-2 hover:bg-accent rounded-md text-left"
                    >
                      <Folder className="h-5 w-5 text-blue-500" />
                      <span className="flex-1 truncate">{dir.name}</span>
                    </button>
                  ))}

                  {/* Empty state */}
                  {directories.length === 0 && !loading && (
                    <div className="text-center py-8 text-muted-foreground">
                      <Folder className="h-12 w-12 mx-auto mb-3 opacity-20" />
                      <p>No subdirectories in this folder</p>
                      <p className="text-sm mt-1">You can select this folder or navigate up</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Selected folder preview */}
            <div className="py-2 border-t">
              <p className="text-sm">
                <span className="text-muted-foreground">Current folder: </span>
                <span className="font-medium">{currentPath}</span>
              </p>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleSelectCurrentFolder}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
              >
                <Check className="h-4 w-4 mr-1" />
                Select Folder
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // Standard mode: label + input + button
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Input
            value={value}
            readOnly
            placeholder={placeholder}
            disabled={disabled}
            className="pr-8"
          />
          {value && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={handleOpenDialog}
          disabled={disabled}
        >
          Browse
        </Button>
      </div>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Select Folder for {label}</DialogTitle>
            <DialogDescription>
              Navigate to and select the folder where files should be stored
            </DialogDescription>
          </DialogHeader>

          {/* Current path and navigation */}
          <div className="flex items-center gap-2 py-2 border-b">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleNavigateUp}
              disabled={currentPath === '/' || loading}
            >
              <ArrowUp className="h-4 w-4 mr-1" />
              Up
            </Button>
            <span className="text-sm text-muted-foreground truncate flex-1">
              {currentPath}
            </span>
            <Button
              type="button"
              size="sm"
              onClick={handleSelectCurrentFolder}
              disabled={loading}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <Check className="h-4 w-4 mr-1" />
              Select This Folder
            </Button>
          </div>

          {/* Error message */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-md">
              <AlertCircle className="h-4 w-4" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* Directory browser */}
          <div className="flex-1 overflow-auto min-h-[300px]">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className="space-y-1">
                {/* Directories */}
                {directories.map((dir) => (
                  <button
                    key={dir.path}
                    type="button"
                    onClick={() => handleNavigateToDirectory(dir.path)}
                    className="w-full flex items-center gap-3 px-3 py-2 hover:bg-accent rounded-md text-left"
                  >
                    <Folder className="h-5 w-5 text-blue-500" />
                    <span className="flex-1 truncate">{dir.name}</span>
                  </button>
                ))}

                {/* Empty state */}
                {directories.length === 0 && !loading && (
                  <div className="text-center py-8 text-muted-foreground">
                    <Folder className="h-12 w-12 mx-auto mb-3 opacity-20" />
                    <p>No subdirectories in this folder</p>
                    <p className="text-sm mt-1">You can select this folder or navigate up</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Selected folder preview */}
          <div className="py-2 border-t">
            <p className="text-sm">
              <span className="text-muted-foreground">Current folder: </span>
              <span className="font-medium">{currentPath}</span>
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSelectCurrentFolder}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <Check className="h-4 w-4 mr-1" />
              Select Folder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FtpFolderPicker;
